import BaseMapLayer from "../components/map/BaseMapLayer";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTheme } from '@mui/material';
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import { Box, IconButton } from "@mui/material";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { MapContainer, GeoJSON } from "react-leaflet";
import { subDays } from 'date-fns';
import dayjs from 'dayjs';

import "leaflet/dist/leaflet.css";

import "./Radar.css";

import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useReviewTargetContext } from "../context/reviewTarget";
import ZoomTracker from "../components/map/ZoomTracker";
import LocationStatusBar from "../components/map/LocationStatusBar";
import FlightMarker from "../components/map/FlightMarker";
import MapEvents from "../components/map/MapEvents";
import LinkedBorders from "../components/map/LinkedBorders";
import { linkedCountries } from "../constants/linkedCountries";
import tanzaniaGeoJSON1 from "../assets/TZA-ADM1.json";
import tanzaniaGeoJSON2 from "../assets/TZA_adm2.json";
import RadarScan from "../components/map/RadarScan";
import SettingsToggleButton from "../components/map/SettingsToggleButton";
import RadarSettingsSidebar from "../components/map/RadarSettingsSidebar";
import CenterMap from "../components/map/CenterMap";
import AzimuthRuler from "../components/map/AzimuthRuler";
import AzimuthDistanceStatusBar from "../components/map/AzimuthDistanceStatusBar";
import socket from "../utils/socket";
import { decodeParams } from "../utils/cryptoUtil";
import AirportMarker from "../components/map/AirportMarker";
import UnitsSidebar from "../components/map/UnitsSidebar";
import { SIDEBAR_WIDTH, UNITS_SIDEBAR_WIDTH } from "../components/map/sidebarWidths";
import { ShowNumberModes, SpeedUnits } from "../config/types";
import OutpostMarker from "../components/map/OutpostMarker";
import TargetsState from "../components/map/TargetsState";
import GrayOverlay from "../components/map/GrayOverlay";
import { RegionLayers } from "../components/regions/RegionLayers";
import MapSettingsSidebar from "../components/map/MapSettingsSidebar";
import ReplayControls from "../components/map/ReplayControls";
import MapClock from "../components/map/MapClock";
import MapUnitBadge from "../components/map/MapUnitBadge";

const Review = () => {
  const theme = useTheme();

  const { auth } = useAuthContext();
  const { setTitle } = usePathContext();
  const { reviewTargets, dispatch } = useReviewTargetContext();

  const axiosPrivate = useAxiosPrivate();

  const [notFound, setNotFound] = useState(false);
  const [scale, setScale] = useState(Math.pow(6 / 6, 1.2));
  const [zoom, setZoom] = useState(6); 
  const [position, setPosition] = useState({ lat: -6.369028, lng: 34.888822 });
  const [centerPosition, setCenterPosition] = useState([ -6.369028, 34.888822 ]);

  const [unitsSidebarOpen, setUnitsSidebarOpen] = useState(false);
  const [settingsSidebarOpen, setSettingsSidebarOpen] = useState(false);
  const [mapSettingsSidebarOpen, setMapSettingsSidebarOpen] = useState(false);
  const [radarColor, setRadarColor] = useState("3,168,168");
  const [showRadarStation, setShowRadarStation] = useState(false);
  const [showNumberMode, setShowNumberMode] = useState(ShowNumberModes.SYNTH); // 0=HIDE, 1=TRACK, 2=SYNTH
  const [speedUnit, setSpeedUnit] = useState(SpeedUnits.KNOTS); // KNOTS, KMH, MPH
  const [showBorder, setShowBorder] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [startDate, setStartDate] = useState(() => dayjs(subDays(new Date(), 1)));
  const [endDate, setEndDate] = useState(() => dayjs(new Date()));
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1); // 1x, 2x, 4x, etc.
  const [currentTime, setCurrentTime] = useState(startDate);
  const lastTickRef = useRef(dayjs());
  const fullscreenRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [reviewTime, setReviewTime] = useState(() => dayjs()); // initialize with now
  const [lastFetchTime, setLastFetchTime] = useState(() => dayjs()); // initialize with now
  // Base map: 'roadmap' | 'satellite' (Google) | 'osm' (OpenStreetMap)
  const [mapType, setMapType] = useState('roadmap');
  
  const [radarData, setRadarData] = useState(null);
  const [updateTimeout, setUpdateTimeout] = useState(5);
  const [trackLength, setTrackLength] = useState(20);
  const [targetCounts, setTargetCounts] = useState([]);
  const [showAirports, setShowAirports] = useState(false);
  const [showOutposts, setShowOutposts] = useState(true);
  const [showFlag, setShowFlag] = useState(true);
  const [overlayOpacity, setOverlayOpacity] = useState(0.7);
  const [airportOpacity, setAirportOpacity] = useState(0.3);
  const [outpostOpacity, setOutpostOpacity] = useState(0.5);
  const [showNoFlyRegion, setShowNoFlyRegion] = useState(true);
  const [regionColor, setRegionColor] = useState("115, 31, 31");
  const [regionFillColor, setRegionFillColor] = useState("114, 74, 74");
  // Regions list
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [regionError, setRegionError] = useState("");

  const [enemyFilter, setEnemyFilter] = useState({
    invert: 19,
    sepia: 96,
    hue: -15,
    brightness: 95,
    contrast: 104,
    saturate: 6881,
  });

  const [friendlyFilter, setFriendlyFilter] = useState({
    invert: 42,
    sepia: 88,
    hue: 190,
    brightness: 120,
    contrast: 90,
    saturate: 520,
  });

  const [passFilter, setPassFilter] = useState({
    invert: 85,
    sepia: 47,
    hue: 3,
    brightness: 106,
    contrast: 104,
    saturate: 514,
  });

  const [unknownFilter, setUnknownFilter] = useState({
    invert: 62,
    sepia: 58,
    hue: 295,
    brightness: 102,
    contrast: 101,
    saturate: 2465,
  });

  const buildIconStyle = (f) => ({
    filter: `
      brightness(0)
      saturate(100%)
      invert(${f.invert}%)
      sepia(${f.sepia}%)
      saturate(${f.saturate}%)
      hue-rotate(${f.hue}deg)
      brightness(${f.brightness}%)
      contrast(${f.contrast}%)
    `
  });

  const isValidDayjs = (date) => dayjs.isDayjs(date) && date.isValid();

  const toggleFullscreen = () => {
    const mapEl = document.documentElement;
    if (!document.fullscreenElement) {
      mapEl.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Playback clock: advance the timeline by real elapsed time x speed
  useEffect(() => {
    if (!isPlaying || !startDate || !endDate) return;

    lastTickRef.current = dayjs();

    const interval = setInterval(() => {
      const now = dayjs();
      const elapsed = now.diff(lastTickRef.current, "milliseconds");
      lastTickRef.current = now;

      setCurrentTime((prev) => {
        const next = dayjs(prev).add(elapsed * playSpeed, "ms");
        return next.isAfter(endDate) ? dayjs(endDate) : next;
      });
    }, 100); // update every 100ms for smoothness

    return () => clearInterval(interval);
  }, [isPlaying, playSpeed, startDate, endDate]);

  // Stop at the end of the range (the clock above only clamps)
  useEffect(() => {
    if (isPlaying && !currentTime.isBefore(endDate)) setIsPlaying(false);
  }, [isPlaying, currentTime, endDate]);

  useEffect(() => {
    setSelectedUnit(auth?.rootUnit || null);    
    setCenterPosition(auth?.rootUnit ? [auth.rootUnit.latitude, auth.rootUnit.longitude] : [-6.369028, 34.888822]);
  }, [auth]);

  useEffect(() => {
    if (showRadarStation && selectedUnit && selectedUnit.type === 3 && radarData) {
      setIsPlaying(false);

      const timestamp = radarData?.time || currentTime?.valueOf();
      const date = dayjs(timestamp > 1e12 ? timestamp : timestamp * 1000);

      if(!currentTime.isSame(date)) {
        // console.log(`Setting current time to: ${date.toISOString()}`);
        setCurrentTime(date);
        setReviewTime(date);
      }
    }
  }, [showRadarStation, radarData, selectedUnit, currentTime]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setTick((prev) => prev + 1);
  //   }, 1000); // increments every second

  //   return () => clearInterval(interval);
  // }, []);

  const selectedUnitId = useMemo(
    () => selectedUnit?._id ?? auth?.rootUnit?._id,
    [selectedUnit, auth]
  );

  // ---------------- Replay rules ----------------
  // The server rebuilds tracks by replaying recorded flights in time order into ReviewTarget.
  //  - While playing, every request replays only the flights since the previous request
  //    (processFrom = where we stopped), so nothing is skipped at high speed or replayed twice.
  //  - Any jump (slider, back to start, new dates, other unit) resets the server state (isStart)
  //    and replays a short warm-up before the new time, so tracks show their recent history.
  //  - The map shows targets updated in the last DISPLAY_MS of the timeline.
  //  - One request at a time: overlapping requests would replay the same flights concurrently.
  const WARMUP_MS = 2 * 60 * 1000;
  const DISPLAY_MS = 60 * 1000;
  const PLAY_POLL_MS = 1000;

  const replayRef = useRef({ processedUntil: null, needReset: true, inFlight: false, pending: false });
  const abortRef = useRef(null);

  const reviewTimeRef = useRef(reviewTime);
  const isPlayingRef = useRef(isPlaying);
  const authRef = useRef(auth);
  const showRadarStationRef = useRef(showRadarStation);
  const currentTimeRef = useRef(currentTime);
  const trackLengthRef = useRef(trackLength);
  const selectedUnitIdRef = useRef(selectedUnitId);

  reviewTimeRef.current = reviewTime;
  isPlayingRef.current = isPlaying;
  authRef.current = auth;
  showRadarStationRef.current = showRadarStation;
  trackLengthRef.current = trackLength;
  selectedUnitIdRef.current = selectedUnitId;
  // seekTo also sets this directly, so a jump is used before the next render
  currentTimeRef.current = currentTime;

  const fetchTargets = async () => {
    const r = replayRef.current;
    const unitId = selectedUnitIdRef.current;
    if (!authRef.current || !unitId) return;

    if (r.inFlight) {           // finish the running replay first, then catch up
      r.pending = true;
      return;
    }

    const radar = showRadarStationRef.current;
    let params;
    let reset = false;
    let endTime;

    if (radar) {
      // Radar station: the time comes from the radar, the server only reads stored targets
      endTime = reviewTimeRef.current;
      params = { startDate: endTime.subtract(5, "minutes"), endDate: endTime };
    } else {
      if (!isPlayingRef.current && !r.needReset) return;   // paused: nothing new to replay

      endTime = currentTimeRef.current;
      reset = r.needReset || !r.processedUntil || r.processedUntil.isAfter(endTime);
      const processFrom = reset ? endTime.subtract(WARMUP_MS, "ms") : r.processedUntil;
      if (!reset && !processFrom.isBefore(endTime)) return;  // timeline hasn't moved

      params = {
        startDate: endTime.subtract(DISPLAY_MS, "ms"),
        endDate: endTime,
        processFrom,
      };
      r.needReset = false;
    }

    r.inFlight = true;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await axiosPrivate.get("/api/reviewTargets/getReviewTargets", {
        params: {
          selectedUnitId: unitId,
          startDate: params.startDate.toISOString(),
          endDate: params.endDate.toISOString(),
          ...(params.processFrom ? { processFrom: params.processFrom.toISOString() } : {}),
          isStart: reset,
          showRadarStation: radar,
          trackLength: trackLengthRef.current,
        },
        signal: controller.signal,
      });

      if (!radar) r.processedUntil = endTime;
      dispatch({ type: "SET_REVIEW_TARGET", payload: res.data });
      setNotFound(false);
      setLastFetchTime(endTime);
    } catch (err) {
      if (reset) r.needReset = true;     // try the reset again next time
      if (err.name !== "CanceledError") {
        console.error("Failed to fetch review targets:", err);
        setNotFound(true);
      }
    } finally {
      r.inFlight = false;
      if (r.pending) {
        r.pending = false;
        fetchTargetsRef.current();
      }
    }
  };

  const fetchTargetsRef = useRef(fetchTargets);
  fetchTargetsRef.current = fetchTargets;

  // Jump to a time: rebuild the server-side tracks around it and show it right away (even paused)
  const seekTo = (time, start = startDate, end = endDate) => {
    let t = dayjs(time);
    if (t.isBefore(start)) t = dayjs(start);
    if (t.isAfter(end)) t = dayjs(end);

    setCurrentTime(t);
    currentTimeRef.current = t;
    lastTickRef.current = dayjs();
    replayRef.current.needReset = true;
    fetchTargetsRef.current();
  };

  // Polling: every second while playing, at the user's interval for the radar station
  useEffect(() => {
    setTitle("Review Flights");

    const ms = showRadarStation ? updateTimeout * 1000 : PLAY_POLL_MS;
    const interval = setInterval(() => fetchTargetsRef.current(), ms);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRadarStation, updateTimeout]);

  // Start of playback: catch up immediately instead of waiting for the first poll
  useEffect(() => {
    if (isPlaying) fetchTargetsRef.current();
  }, [isPlaying]);

  // New unit, or radar-station mode switched: rebuild from scratch at the current time
  useEffect(() => {
    dispatch({ type: "SET_REVIEW_TARGET", payload: [] });
    replayRef.current.needReset = true;
    replayRef.current.processedUntil = null;
    fetchTargetsRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitId, showRadarStation]);

  // Cancel a running request when leaving the page
  useEffect(() => () => abortRef.current?.abort(), []);

  const handlePlayPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (!currentTime.isBefore(endDate)) seekTo(startDate);   // at the end: play again from the start
    setIsPlaying(true);
  };

  const handlePlaySpeed = (speed) => {
    setPlaySpeed(speed);
    if (!isPlaying) {
      if (!currentTime.isBefore(endDate)) seekTo(startDate);
      setIsPlaying(true);
    }
  };

  // When user changes startDate or endDate (the pickers also block an inverted range)
  const handleStartDateChange = (newValue) => {
    if (!isValidDayjs(newValue) || !newValue.isBefore(endDate)) return;
    setStartDate(newValue);
    seekTo(newValue, newValue, endDate);
  };

  const handleEndDateChange = (newValue) => {
    if (!isValidDayjs(newValue) || !newValue.isAfter(startDate)) return;
    setEndDate(newValue);
    if (currentTime.isAfter(newValue)) seekTo(newValue, startDate, newValue);
  };

  // Activity graph above the slider, for the current unit and range
  useEffect(() => {
    if (!auth || !selectedUnitId || !isValidDayjs(startDate) || !isValidDayjs(endDate)) return;

    const controller = new AbortController();

    axiosPrivate
      .get("/api/reviewTargets/getReviewTargetCounts", {
        params: {
          selectedUnitId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
        signal: controller.signal,
      })
      .then((res) => setTargetCounts(res.data))
      .catch((err) => {
        if (err.name !== "CanceledError") console.error("Failed to fetch target counts:", err);
      });

    return () => controller.abort();
  }, [auth, selectedUnitId, startDate, endDate, axiosPrivate]);

  useEffect(() => {
  
    let isMounted = true;

    const loadRegions = async () => {
      setLoadingRegions(true);
      setRegionError("");

      try {
        const response = await axiosPrivate.get("/api/regions");
        if (isMounted) {
          setRegions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error("Error fetching regions:", err);
        if (isMounted) {
          setRegionError(err.response?.data?.error || "Failed to load regions.");
        }
      } finally {
        if (isMounted) setLoadingRegions(false);
      }
    };

    loadRegions();

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate]);
  
  useEffect(() => {
    if(!showRadarStation || !selectedUnit || selectedUnit.type !== 3){
      if(!showRadarStation) 
        setRadarData(null);
      return;
    }

    const handleRadarReview = (data) => {
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        const radarData = parsed;
        // const radarData = decodeParams(parsed.payload);
        setRadarData(radarData);
      } catch (err) {
        console.error('Failed to process radarReview:', err);
      }
    };

    socket.on("radarReview", handleRadarReview);

    // Clean up on unmount or when dependencies change
    return () => {
      socket.off("radarReview", handleRadarReview);
    };
  }, [showRadarStation, selectedUnit]);
  
  const processedTargets = useMemo(() => {
    const filtered = [];
    const tracks = {};
    const outposts = {};

    if (!reviewTargets?.length) return { filtered, tracks, outposts };

    for (let i = 0; i < reviewTargets.length; i++) {
      const target = reviewTargets[i];
      if (target.typeName === "UNKNOWN") continue;

      filtered.push(target);

      // Group by Track ID
      const trackKey = target.trackId;
      if (!tracks[trackKey]) tracks[trackKey] = [];
      tracks[trackKey].push(target);

      // Group by Outpost Numbers
      if (target.outpostNums && Array.isArray(target.outpostNums)) {
        for (let j = 0; j < target.outpostNums.length; j++) {
          const outpostNum = target.outpostNums[j];
          if (!outposts[outpostNum]) outposts[outpostNum] = [];
          outposts[outpostNum].push(target);
        }
      }
    }

    return { filtered, tracks, outposts };
  }, [reviewTargets]);

  const filteredTargets = processedTargets.filtered;
  const groupedTargets = processedTargets.tracks;
  const groupedTargetsByOutpostNum = processedTargets.outposts;

  const azimuthRulerElement = useMemo(() => {
    if (showRadarStation && selectedUnit && selectedUnit.type === 3) {
      return (
        <>
          <AzimuthRuler
            center={centerPosition}
            radius={500000}
            step={10}
            ringInterval={50000}
            beamWidth={1}
            rotationSpeed={1.2}
            threatLevel={"normal"}
            color={radarColor}
          />

          <RadarScan
            center={{ lat: centerPosition[0], lng: centerPosition[1] }}
            radiusKm={500}
            beamAngle={1}
            rotationSpeed={1.2}
            trailSteps={15}
            color={radarColor}
            ringCount={Math.floor(500000 / 50000)}
            radarData={radarData}
            showNoData={true}
          />
        </>
      );
    }
    return null;
  }, [showRadarStation, selectedUnit, centerPosition, radarColor, radarData]);

  return (
    <Box
      ref={fullscreenRef}
      flex={1}
      position="relative"
      className={isFullscreen ? "fullscreen" : ""}
    >     
      <Box m="1rem" height="89vh" display="flex" borderRadius="4px" position="relative">
        {/* Map */}
        <Box 
          flex={1} 
          position="relative" 
          className={isFullscreen ? "fullscreen" : ""}
        >
          <SettingsToggleButton 
          onUnitsClick={() => {
              setUnitsSidebarOpen((o) => !o);
              setSettingsSidebarOpen(false);
              setMapSettingsSidebarOpen(false);
          }} 
          onSettingsClick={() => {
            setSettingsSidebarOpen((o) => !o);
            setUnitsSidebarOpen(false);
            setMapSettingsSidebarOpen(false);
          }} 
          onMapSettingsClick={() => {
            setMapSettingsSidebarOpen((o) => !o);
            setUnitsSidebarOpen(false);
            setSettingsSidebarOpen(false);
          }}
          isOpen={unitsSidebarOpen || settingsSidebarOpen || mapSettingsSidebarOpen}
          sidebarWidth={unitsSidebarOpen ? UNITS_SIDEBAR_WIDTH : SIDEBAR_WIDTH} 
          active={unitsSidebarOpen ? 'units' : settingsSidebarOpen ? 'settings' : mapSettingsSidebarOpen ? 'map' : null}
          mode="replay"
        />
          <TargetsState
            mode="replay"
            targets={groupedTargetsByOutpostNum}
            isOpen={unitsSidebarOpen || settingsSidebarOpen || mapSettingsSidebarOpen}
            sidebarWidth={unitsSidebarOpen ? UNITS_SIDEBAR_WIDTH : SIDEBAR_WIDTH} 
            foeFilters={{
              enemy: enemyFilter,
              friendly: friendlyFilter,
              pass: passFilter,
              unknown: unknownFilter,
            }}
          />

          {/* Clock */}
          <MapClock mode="replay" time={currentTime} isPlaying={isPlaying} speed={playSpeed} />

          {/* Selected unit */}
          <MapUnitBadge unit={selectedUnit} mode="replay" radarView={showRadarStation && selectedUnit?.type === 3} />

          { radarData === null && (
            <ReplayControls
              startDate={isValidDayjs(startDate) ? startDate : dayjs()}
              endDate={isValidDayjs(endDate) ? endDate : dayjs()}
              currentTime={currentTime}
              isPlaying={isPlaying}
              playSpeed={playSpeed}
              targetCounts={targetCounts}
              onStartChange={handleStartDateChange}
              onEndChange={handleEndDateChange}
              onPlayPause={handlePlayPause}
              onSpeed={handlePlaySpeed}
              onScrub={setCurrentTime}
              onSeek={(t) => seekTo(t)}
            />
          )}
          
          <MapContainer
            center={centerPosition}
            zoom={zoom + 0.5}     // offset by 0.5 zoom
            zoomSnap={0}          // allow fractional zoom
            zoomDelta={0.25}      // optional: makes scroll smoother
            style={{ height: "100%", width: "100%" }}
          >
            <CenterMap center={centerPosition} zoom={zoom + 0.5} />  {/* moves and zooms together; zoom offset by 0.5 */}
            
            <ZoomTracker setScale={setScale} />

            {/* Fullscreen Toggle Button */}
            <Box
              position="absolute"
              top="86px"
              left="12px"
              zIndex={1000}
            >
              <IconButton
                onClick={toggleFullscreen}
                size="small"
                sx={{
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Box>

            <BaseMapLayer type={mapType} />

             { showAirports && (
                <>
                  <AirportMarker 
                    center={{ lat: centerPosition[0], lng: centerPosition[1] }}
                    scale={scale} 
                    opacity={airportOpacity}/>
                </>
              )}
    
              { showOutposts && (
                <OutpostMarker opacity={outpostOpacity} />
              )}

            <GrayOverlay overlayOpacity={overlayOpacity} />
          
            { showBorder && (
              <>
                <LinkedBorders countries={linkedCountries} />

                <GeoJSON
                  data={tanzaniaGeoJSON1}
                  style={{
                    color: "#FFD700",
                    weight: 0.3,
                    fillOpacity: 0.05,
                    fillColor: "#A2E4D7",
                  }}
                />
              </>
            )}

            { showNoFlyRegion && (
              <RegionLayers
                regions={regions}
                selectedId={null}
                color={`rgb(${regionColor})`}
                fillColor={`rgb(${regionFillColor})`}
              />
            )}  

            {azimuthRulerElement}
           
            <FlightMarker
              groupedTargets={groupedTargets} 
              scale={scale} 
              showNumberMode={showNumberMode}
              currentTime={lastFetchTime}
              showFlag={showFlag}
              foeFilters={{
                enemy: enemyFilter,
                friendly: friendlyFilter,
                pass: passFilter,
                unknown: unknownFilter,
              }}
            />
            <MapEvents setPosition={setPosition} />
          </MapContainer>

          { showRadarStation && selectedUnit && selectedUnit.type === 3 && (          
            <AzimuthDistanceStatusBar mode="replay" from={selectedUnit} to={position} />
          )}

          <LocationStatusBar mode="replay" position={position} />

          {unitsSidebarOpen && (
            <UnitsSidebar
              mode="replay"
              selectedUnit={selectedUnit}
              setSelectedUnit={setSelectedUnit}
              setCenterPosition={setCenterPosition}
              showRadarStation={showRadarStation}
              setShowRadarStation={setShowRadarStation}
              zoom={zoom}
              setZoom={setZoom}
              
            />          
          )}
          {settingsSidebarOpen && (
            <RadarSettingsSidebar
              mode="replay"
              selectedUnit={selectedUnit}
              setSelectedUnit={setSelectedUnit}            
              showNumberMode={showNumberMode}
              setShowNumberMode={setShowNumberMode}
              speedUnit={speedUnit}
              setSpeedUnit={setSpeedUnit}
              radarColor={radarColor}
              setRadarColor={setRadarColor}            
              updateTimeout={updateTimeout}
              setUpdateTimeout={setUpdateTimeout}
              trackLength={trackLength}
              setTrackLength={setTrackLength}            
              showFlag={showFlag}
              setShowFlag={setShowFlag}            
              enemyFilter={enemyFilter}
              setEnemyFilter={setEnemyFilter}
              friendlyFilter={friendlyFilter}
              setFriendlyFilter={setFriendlyFilter}
              passFilter={passFilter}
              setPassFilter={setPassFilter}
              unknownFilter={unknownFilter}
              setUnknownFilter={setUnknownFilter}
            />
          )}

          {mapSettingsSidebarOpen && (
            <MapSettingsSidebar
              mode="replay"
              mapType={mapType}
              setMapType={setMapType}
              showBorder={showBorder}
              setShowBorder={setShowBorder}
              showAirports={showAirports}
              setShowAirports={setShowAirports}
              showOutposts={showOutposts}
              setShowOutposts={setShowOutposts}
              outpostOpacity={outpostOpacity}
              setOutpostOpacity={setOutpostOpacity}
              overlayOpacity={overlayOpacity}
              setOverlayOpacity={setOverlayOpacity}
              showNoFlyRegion={showNoFlyRegion}
              setShowNoFlyRegion={setShowNoFlyRegion}
              noFlyRegionColor={regionColor}
              setNoFlyRegionColor={setRegionColor}
              noFlyRegionFillColor={regionFillColor}
              setNoFlyRegionFillColor={setRegionFillColor}
              airportOpacity={airportOpacity}
              setAirportOpacity={setAirportOpacity}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Review;